import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketAPI, walletAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useFarm } from '../contexts/FarmContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatBusinessTypes } from '../constants/businessTypes';
import toast from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  BuildingStorefrontIcon, 
  MapPinIcon,
  ShoppingCartIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  TruckIcon,
  PhoneIcon,
  WalletIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const KENYA_COUNTIES = [
  'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
  'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
  'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos',
  'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', "Murang'a", 'Nairobi',
  'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri', 'Samburu', 'Siaya',
  'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans-Nzoia', 'Turkana', 'Uasin Gishu',
  'Vihiga', 'Wajir', 'West Pokot'
];

const CATEGORIES = [
  { id: 'feed', name: 'Feed' },
  { id: 'equipment', name: 'Equipment' },
  { id: 'medication', name: 'Medication' },
  { id: 'chicks', name: 'Day-old Chicks' },
  { id: 'other', name: 'Other' }
];

const Market = () => {
  const { user } = useAuth();
  const { currentFarm } = useFarm();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('suppliers'); // 'suppliers', 'products', or 'orders'
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState({ county: '', category: '', search: '', businessType: '' });
  
  // Order state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [cart, setCart] = useState([]); // [{product, quantity}]
  const [showCheckout, setShowCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // My Orders state
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFilters, setOrderFilters] = useState({ status: '', search: '' });
  const [ordersPage, setOrdersPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const ORDERS_PER_PAGE = 10;
  
  // Payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [mpesaSent, setMpesaSent] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);

  useEffect(() => {
    loadData();
  }, [tab, filter.county, filter.category, filter.businessType]);
  
  // Load orders when orders tab is selected
  useEffect(() => {
    if (tab === 'orders') {
      loadMyOrders();
    }
  }, [tab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (tab === 'suppliers') {
        const params = {};
        if (filter.county) params.county = filter.county;
        if (filter.businessType) params.businessType = filter.businessType;
        const res = await marketAPI.listSuppliers(params);
        setSuppliers(res.data.suppliers || []);
      } else if (tab === 'products') {
        const params = {};
        if (filter.county) params.county = filter.county;
        if (filter.category) params.category = filter.category;
        const res = await marketAPI.listSupplierProducts(params);
        setProducts(res.data.products || []);
      }
      // Orders are loaded separately via loadMyOrders
    } catch (error) {
      console.error('Load market data error:', error);
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadMyOrders = async () => {
    try {
      setOrdersLoading(true);
      const params = {};
      if (orderFilters.status) params.status = orderFilters.status;
      const res = await marketAPI.getMySupplierOrders(params);
      setMyOrders(res.data.orders || []);
    } catch (error) {
      console.error('Load my orders error:', error);
      toast.error('Failed to load your orders');
    } finally {
      setOrdersLoading(false);
    }
  };
  
  // Filtered and paginated orders
  const filteredOrders = useMemo(() => {
    let orders = myOrders;
    if (orderFilters.status) {
      orders = orders.filter(o => o.status === orderFilters.status);
    }
    if (orderFilters.search) {
      const q = orderFilters.search.toLowerCase();
      orders = orders.filter(o => 
        o.orderNumber?.toLowerCase().includes(q) ||
        o.supplier?.businessName?.toLowerCase().includes(q)
      );
    }
    return orders;
  }, [myOrders, orderFilters]);
  
  const paginatedOrders = useMemo(() => {
    const start = (ordersPage - 1) * ORDERS_PER_PAGE;
    return filteredOrders.slice(start, start + ORDERS_PER_PAGE);
  }, [filteredOrders, ordersPage]);
  
  const totalOrderPages = Math.ceil(filteredOrders.length / ORDERS_PER_PAGE);

  // Filter by search
  const filteredSuppliers = useMemo(() => {
    if (!filter.search) return suppliers;
    const q = filter.search.toLowerCase();
    return suppliers.filter(s => 
      s.businessName?.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q) ||
      s.county?.toLowerCase().includes(q)
    );
  }, [suppliers, filter.search]);

  const filteredProducts = useMemo(() => {
    if (!filter.search) return products;
    const q = filter.search.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      p.brand?.toLowerCase().includes(q)
    );
  }, [products, filter.search]);

  // Load supplier products
  const loadSupplierProducts = async (supplier) => {
    try {
      const res = await marketAPI.getSupplierProducts(supplier.id);
      setSelectedSupplier({ ...supplier, products: res.data.products || [] });
      setCart([]);
    } catch (error) {
      toast.error('Failed to load supplier products');
    }
  };

  // Cart management
  const addToCart = (product, qty = 1) => {
    setCart(prev => {
      const existing = prev.find(c => c.product.id === product.id);
      if (existing) {
        return prev.map(c => 
          c.product.id === product.id 
            ? { ...c, quantity: c.quantity + qty }
            : c
        );
      }
      return [...prev, { product, quantity: qty }];
    });
    toast.success('Added to cart');
  };

  const updateCartQty = (productId, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(c => c.product.id !== productId));
    } else {
      setCart(prev => prev.map(c => 
        c.product.id === productId ? { ...c, quantity: qty } : c
      ));
    }
  };

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, c) => sum + (Number(c.product.price) * c.quantity), 0);
  }, [cart]);

  // Submit order
  const submitOrder = async (formData) => {
    if (!selectedSupplier || cart.length === 0) return;
    
    setSubmitting(true);
    try {
      const payload = {
        supplierId: selectedSupplier.id,
        farmId: currentFarm?.id || null,
        items: cart.map(c => ({
          productId: c.product.id,
          quantity: c.quantity
        })),
        fulfillmentType: formData.fulfillmentType,
        deliveryAddress: formData.deliveryAddress,
        deliveryCounty: formData.deliveryCounty,
        deliveryTown: formData.deliveryTown,
        deliveryNotes: formData.deliveryNotes,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
        notes: formData.notes
      };

      const orderRes = await marketAPI.placeSupplierOrder(payload);
      const orderId = orderRes.data.order?.id;
      
      // If paying with wallet, process the wallet payment
      if (formData.paymentMethod === 'wallet' && orderId) {
        try {
          await walletAPI.payWithWallet(orderId);
          toast.success('Order placed and paid with wallet! Funds held in escrow until delivery.');
        } catch (walletError) {
          toast.success('Order placed! Wallet payment failed - please pay later.');
          console.error('Wallet payment error:', walletError);
        }
      } else {
        toast.success('Order placed successfully!');
      }
      
      setShowCheckout(false);
      setCart([]);
      setSelectedSupplier(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to place order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 text-white">
        <div className="absolute top-0 right-0 w-40 sm:w-64 h-40 sm:h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 sm:w-40 h-24 sm:h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 bg-white/20 backdrop-blur rounded-lg sm:rounded-xl">
              <ShoppingCartIcon className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Marketplace</h1>
              <p className="text-green-100 text-xs sm:text-sm">Buy feed, equipment, medication, and chicks</p>
            </div>
          </div>
          {cart.length > 0 && selectedSupplier && (
            <button
              onClick={() => setShowCheckout(true)}
              className="inline-flex items-center justify-center px-4 py-2 sm:px-5 sm:py-2.5 bg-white text-green-700 rounded-lg sm:rounded-xl hover:bg-green-50 transition-colors font-medium text-sm"
            >
              <ShoppingCartIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2" />
              Checkout ({cart.length})
            </button>
          )}
        </div>
        
        {/* Stats */}
        <div className="relative mt-4 sm:mt-6 grid grid-cols-3 gap-2 sm:gap-3">
          <div className="bg-white/10 backdrop-blur rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <p className="text-lg sm:text-2xl font-bold">{suppliers.length}</p>
            <p className="text-green-200 text-[10px] sm:text-xs">Suppliers</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <p className="text-lg sm:text-2xl font-bold">{products.length}</p>
            <p className="text-green-200 text-[10px] sm:text-xs">Products</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <p className="text-lg sm:text-2xl font-bold">{myOrders.length}</p>
            <p className="text-green-200 text-[10px] sm:text-xs">My Orders</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex border-b border-gray-100 overflow-x-auto">
          <button
            onClick={() => { setTab('suppliers'); setSelectedSupplier(null); }}
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              tab === 'suppliers' && !selectedSupplier
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Suppliers
          </button>
          <button
            onClick={() => { setTab('products'); setSelectedSupplier(null); }}
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              tab === 'products' && !selectedSupplier
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Products
          </button>
          <button
            onClick={() => { setTab('orders'); setSelectedSupplier(null); }}
            className={`flex-1 sm:flex-none px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              tab === 'orders'
                ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            My Orders
          </button>
          {selectedSupplier && (
            <span className="hidden sm:flex flex-1 sm:flex-none px-6 py-4 text-sm font-medium text-green-600 border-b-2 border-green-500 bg-green-50 whitespace-nowrap">
              {selectedSupplier.businessName}
            </span>
          )}
        </div>
      </div>

      {/* Filters - only for suppliers and products tabs */}
      {!selectedSupplier && tab !== 'orders' && (
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-2 sm:top-2.5 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <input
                type="text"
                placeholder={tab === 'suppliers' ? 'Search suppliers...' : 'Search products...'}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              />
            </div>
            <div className="flex gap-2">
              <select
                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                value={filter.county}
                onChange={(e) => setFilter({ ...filter, county: e.target.value })}
              >
                <option value="">All Counties</option>
                {KENYA_COUNTIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {tab === 'products' && (
                <select
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  value={filter.category}
                  onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                >
                  <option value="">All Categories</option>
                  {CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              )}
              {tab === 'suppliers' && (
                <select
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  value={filter.businessType}
                  onChange={(e) => setFilter({ ...filter, businessType: e.target.value })}
                >
                  <option value="">All Types</option>
                  <option value="feed">Feed Suppliers</option>
                  <option value="equipment">Equipment</option>
                  <option value="medication">Medication</option>
                  <option value="hatchery">Hatcheries</option>
                  <option value="veterinary">Veterinary</option>
                </select>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {loading && tab !== 'orders' ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : selectedSupplier ? (
        // Supplier Detail View
        <div className="space-y-4 sm:space-y-6">
          {/* Mobile back button */}
          <button
            onClick={() => setSelectedSupplier(null)}
            className="sm:hidden text-sm text-green-600 font-medium flex items-center"
          >
            ← Back to suppliers
          </button>
          
          {/* Supplier Header */}
          <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {selectedSupplier.logoUrl ? (
                    <img src={selectedSupplier.logoUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <BuildingStorefrontIcon className="h-7 w-7 sm:h-10 sm:w-10 text-blue-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <h2 className="text-base sm:text-xl font-bold text-gray-900">{selectedSupplier.businessName}</h2>
                    {selectedSupplier.isVerified && (
                      <CheckBadgeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 flex-shrink-0" title="Verified" />
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-500">{formatBusinessTypes(selectedSupplier)}</p>
                  {selectedSupplier.county && (
                    <p className="text-xs sm:text-sm text-gray-600 flex items-center mt-0.5 sm:mt-1">
                      <MapPinIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                      {[selectedSupplier.town, selectedSupplier.county].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              </div>
              {selectedSupplier.description && (
                <p className="text-xs sm:text-sm text-gray-600 sm:hidden">{selectedSupplier.description}</p>
              )}
              <div className="hidden sm:block flex-1">
                {selectedSupplier.description && (
                  <p className="text-sm text-gray-600 mt-2">{selectedSupplier.description}</p>
                )}
                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                  {selectedSupplier.deliveryAvailable && (
                    <span className="flex items-center">
                      <TruckIcon className="h-4 w-4 mr-1" />
                      Delivery available
                    </span>
                  )}
                  {selectedSupplier.contactPhone && (
                    <span className="flex items-center">
                      <PhoneIcon className="h-4 w-4 mr-1" />
                      {selectedSupplier.contactPhone}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelectedSupplier(null)}
                className="hidden sm:block text-gray-400 hover:text-gray-600 text-sm"
              >
                Back to list
              </button>
            </div>
            {/* Mobile delivery/phone info */}
            <div className="sm:hidden flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
              {selectedSupplier.deliveryAvailable && (
                <span className="flex items-center bg-green-50 text-green-700 px-2 py-1 rounded-full">
                  <TruckIcon className="h-3.5 w-3.5 mr-1" />
                  Delivery
                </span>
              )}
              {selectedSupplier.contactPhone && (
                <a href={`tel:${selectedSupplier.contactPhone}`} className="flex items-center bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                  <PhoneIcon className="h-3.5 w-3.5 mr-1" />
                  {selectedSupplier.contactPhone}
                </a>
              )}
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {(selectedSupplier.products || []).length === 0 ? (
              <div className="col-span-full text-center py-8 sm:py-12">
                <p className="text-gray-500 text-sm">No products available from this supplier</p>
              </div>
            ) : (
              selectedSupplier.products.map(product => (
                <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="aspect-video bg-gray-100">
                    {(product.images?.length > 0 || product.imageUrl) ? (
                      <img 
                        src={product.images?.[0] || product.imageUrl} 
                        alt={product.name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <BuildingStorefrontIcon className="h-8 w-8 sm:h-12 sm:w-12" />
                      </div>
                    )}
                  </div>
                  <div className="p-2.5 sm:p-4">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-1">{product.name}</h3>
                    {product.brand && <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{product.brand}</p>}
                    {product.description && (
                      <p className="hidden sm:block text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2 sm:mt-3">
                      <p className="text-sm sm:text-lg font-bold text-gray-900">
                        KES {Number(product.price).toLocaleString()}
                        {product.unit && <span className="text-xs sm:text-sm font-normal text-gray-500">/{product.unit}</span>}
                      </p>
                      <button
                        onClick={() => addToCart(product)}
                        className="w-full sm:w-auto px-2.5 sm:px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Cart Summary - Fixed bottom for mobile */}
          {cart.length > 0 && (
            <>
              {/* Mobile: Bottom bar */}
              <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-40">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs text-gray-500">{cart.length} item(s)</p>
                    <p className="font-bold text-gray-900">KES {cartTotal.toLocaleString()}</p>
                  </div>
                  <button
                    onClick={() => setShowCheckout(true)}
                    className="flex-1 max-w-[180px] py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                  >
                    Checkout
                  </button>
                </div>
              </div>
              {/* Desktop: Floating card */}
              <div className="hidden sm:block fixed bottom-4 right-4 bg-white rounded-xl shadow-lg border border-gray-200 p-4 w-72 z-40">
                <h4 className="font-semibold text-gray-900 mb-3">Cart ({cart.length} items)</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {cart.map(c => (
                    <div key={c.product.id} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 truncate">{c.product.name}</span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateCartQty(c.product.id, c.quantity - 1)}
                          className="w-6 h-6 bg-gray-100 rounded text-gray-600"
                        >
                          -
                        </button>
                        <span>{c.quantity}</span>
                        <button
                          onClick={() => updateCartQty(c.product.id, c.quantity + 1)}
                          className="w-6 h-6 bg-gray-100 rounded text-gray-600"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span>KES {cartTotal.toLocaleString()}</span>
                  </div>
                  <button
                    onClick={() => setShowCheckout(true)}
                    className="w-full mt-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Checkout
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : tab === 'suppliers' ? (
        // Suppliers List
        filteredSuppliers.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center border border-gray-100">
            <BuildingStorefrontIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No suppliers found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {filteredSuppliers.map(supplier => (
              <div 
                key={supplier.id} 
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => loadSupplierProducts(supplier)}
              >
                <div className="h-24 sm:h-32 bg-gradient-to-br from-blue-100 to-indigo-100 relative">
                  {supplier.logoUrl ? (
                    <img src={supplier.logoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <BuildingStorefrontIcon className="h-8 w-8 sm:h-12 sm:w-12 text-blue-300" />
                    </div>
                  )}
                  {supplier.isVerified && (
                    <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 bg-blue-500 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full flex items-center">
                      <CheckBadgeIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      Verified
                    </div>
                  )}
                </div>
                <div className="p-3 sm:p-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-1">{supplier.businessName}</h3>
                  <p className="text-xs sm:text-sm text-gray-500">{formatBusinessTypes(supplier)}</p>
                  {supplier.county && (
                    <p className="text-xs sm:text-sm text-gray-600 flex items-center mt-0.5 sm:mt-1">
                      <MapPinIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{[supplier.town, supplier.county].filter(Boolean).join(', ')}</span>
                    </p>
                  )}
                  {supplier.description && (
                    <p className="hidden sm:block text-sm text-gray-600 mt-2 line-clamp-2">{supplier.description}</p>
                  )}
                  <div className="mt-2 sm:mt-3 flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                    {supplier.deliveryAvailable && (
                      <span className="flex items-center bg-green-50 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        <TruckIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        Delivery
                      </span>
                    )}
                    {supplier.productCount > 0 && (
                      <span className="bg-blue-50 text-blue-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        {supplier.productCount} products
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // Products List
        filteredProducts.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center border border-gray-100">
            <BuildingStorefrontIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 sm:mb-2">No products found</h3>
            <p className="text-gray-500 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="aspect-video bg-gray-100">
                  {product.images?.length ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <BuildingStorefrontIcon className="h-8 w-8 sm:h-12 sm:w-12" />
                    </div>
                  )}
                </div>
                <div className="p-2.5 sm:p-4">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-1">{product.name}</h3>
                  {product.supplier && (
                    <p className="text-xs sm:text-sm text-blue-600 line-clamp-1">{product.supplier.businessName}</p>
                  )}
                  {product.brand && <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">{product.brand}</p>}
                  {product.description && (
                    <p className="hidden sm:block text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2 sm:mt-3">
                    <p className="text-sm sm:text-lg font-bold text-gray-900">
                      KES {Number(product.price).toLocaleString()}
                      {product.unit && <span className="text-xs sm:text-sm font-normal text-gray-500">/{product.unit}</span>}
                    </p>
                    <button
                      onClick={() => {
                        if (product.supplier) {
                          loadSupplierProducts(product.supplier);
                        }
                      }}
                      className="w-full sm:w-auto px-2.5 sm:px-3 py-1.5 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700"
                    >
                      View Supplier
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* My Orders Tab */}
      {tab === 'orders' && (
        <div className="space-y-3 sm:space-y-4">
          {/* Order Filters */}
          <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 border border-gray-100">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 sm:items-center">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-2 sm:top-2.5 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  className="w-full pl-9 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={orderFilters.search}
                  onChange={(e) => { setOrderFilters(f => ({ ...f, search: e.target.value })); setOrdersPage(1); }}
                />
              </div>
              <div className="flex gap-2">
                <select
                  className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  value={orderFilters.status}
                  onChange={(e) => { setOrderFilters(f => ({ ...f, status: e.target.value })); setOrdersPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="preparing">Preparing</option>
                  <option value="ready">Ready</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <button
                  onClick={loadMyOrders}
                  className="text-sm text-blue-600 hover:text-blue-700 px-2"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {ordersLoading ? (
            <div className="flex justify-center py-8 sm:py-12">
              <LoadingSpinner />
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center border border-gray-100">
              <ShoppingCartIcon className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900">No orders yet</h3>
              <p className="text-gray-500 mt-1 text-sm">Browse suppliers and place your first order</p>
              <button
                onClick={() => setTab('suppliers')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Browse Suppliers
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Order #</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Supplier</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Items</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {paginatedOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.orderNumber}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{order.supplier?.businessName || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{order.items?.length || 0} item(s)</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">
                            KES {Number(order.totalAmount).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                              order.status === 'confirmed' || order.status === 'preparing' || order.status === 'ready' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                              order.paymentStatus === 'partial' ? 'bg-amber-100 text-amber-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {order.paymentStatus || 'pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {new Date(order.placedAt || order.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedOrder(order)}
                              className="text-blue-600 hover:text-blue-700 text-sm"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalOrderPages > 1 && (
                  <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <span className="text-sm text-gray-600">
                      Showing {(ordersPage - 1) * ORDERS_PER_PAGE + 1} - {Math.min(ordersPage * ORDERS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                        disabled={ordersPage === 1}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">Page {ordersPage} of {totalOrderPages}</span>
                      <button
                        onClick={() => setOrdersPage(p => Math.min(totalOrderPages, p + 1))}
                        disabled={ordersPage === totalOrderPages}
                        className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Cards */}
              <div className="sm:hidden space-y-3">
                {paginatedOrders.map((order) => (
                  <div 
                    key={order.id} 
                    className="bg-white rounded-xl border border-gray-200 p-3.5"
                    onClick={() => setSelectedOrder(order)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{order.orderNumber}</p>
                        <p className="text-xs text-gray-500">{order.supplier?.businessName || 'Unknown'}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'confirmed' || order.status === 'preparing' || order.status === 'ready' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-900">KES {Number(order.totalAmount).toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{order.items?.length || 0} item(s)</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          order.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {order.paymentStatus || 'pending'}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">{new Date(order.placedAt || order.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Mobile Pagination */}
                {totalOrderPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xs text-gray-500">Page {ordersPage}/{totalOrderPages}</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setOrdersPage(p => Math.max(1, p - 1))}
                        disabled={ordersPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs hover:bg-gray-100 disabled:opacity-50"
                      >
                        Prev
                      </button>
                      <button
                        onClick={() => setOrdersPage(p => Math.min(totalOrderPages, p + 1))}
                        disabled={ordersPage === totalOrderPages}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs hover:bg-gray-100 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-start">
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Order #{selectedOrder.orderNumber}</h3>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {selectedOrder.supplier?.businessName} • {new Date(selectedOrder.placedAt || selectedOrder.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Status */}
              <div className="flex flex-wrap gap-3 sm:gap-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span className={`px-2.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm rounded-full ${
                    selectedOrder.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    selectedOrder.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    selectedOrder.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedOrder.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Payment</p>
                  <span className={`px-2.5 sm:px-3 py-0.5 sm:py-1 text-xs sm:text-sm rounded-full ${
                    selectedOrder.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {selectedOrder.paymentStatus || 'pending'}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Order Items</h4>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-xs sm:text-sm">
                      <span className="flex-1 min-w-0 truncate mr-2">{item.quantity}x {item.productName}</span>
                      <span className="font-medium flex-shrink-0">KES {(Number(item.unitPrice) * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span>Subtotal</span>
                      <span>KES {Number(selectedOrder.subtotal).toLocaleString()}</span>
                    </div>
                    {Number(selectedOrder.deliveryFee) > 0 && (
                      <div className="flex justify-between text-xs sm:text-sm">
                        <span>Delivery Fee</span>
                        <span>KES {Number(selectedOrder.deliveryFee).toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-sm">
                      <span>Total</span>
                      <span>KES {Number(selectedOrder.totalAmount).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              {selectedOrder.fulfillmentType === 'delivery' && (
                <div>
                  <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Delivery Address</h4>
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4 text-xs sm:text-sm text-gray-700">
                    <p>{selectedOrder.deliveryAddress}</p>
                    {selectedOrder.deliveryTown && <p>{selectedOrder.deliveryTown}, {selectedOrder.deliveryCounty}</p>}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div>
                <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Contact</h4>
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4 text-xs sm:text-sm text-gray-700">
                  <p>{selectedOrder.contactName}</p>
                  <p>{selectedOrder.contactPhone}</p>
                  {selectedOrder.contactEmail && <p>{selectedOrder.contactEmail}</p>}
                </div>
              </div>

              {/* Payment section if unpaid */}
              {/* Show pending confirmation message for unpaid, pending orders */}
              {selectedOrder.paymentStatus !== 'paid' && selectedOrder.status === 'pending' && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-start gap-2">
                      <svg className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-amber-800">Awaiting Supplier Confirmation</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Payment will be enabled once the supplier confirms your order. You'll be notified when it's ready.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Show pay button for confirmed but unpaid orders */}
              {selectedOrder.paymentStatus !== 'paid' && selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'pending' && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 flex items-start gap-2">
                    <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    Your payment will be held in escrow until delivery is confirmed
                  </p>
                  <button
                    onClick={() => {
                      setPayingOrder(selectedOrder);
                      setMpesaPhone(user?.phone || '');
                      setShowPaymentModal(true);
                      setMpesaSent(false);
                      // Load wallet balance from wallet-check endpoint
                      marketAPI.checkWalletPayment(selectedOrder.id).then(res => {
                        setWalletBalance(Number(res.data.walletBalance || 0));
                      }).catch(() => setWalletBalance(0));
                    }}
                    className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-sm"
                  >
                    Pay Now - KES {Number(selectedOrder.totalAmount).toLocaleString()}
                  </button>
                </div>
              )}

              {/* Confirm Delivery section for shipped and paid orders */}
              {selectedOrder.status === 'shipped' && selectedOrder.paymentStatus === 'paid' && (
                <div className="border-t border-gray-200 pt-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <p className="text-xs sm:text-sm text-blue-800 font-medium mb-1">📦 Your order has been shipped!</p>
                    <p className="text-xs sm:text-sm text-blue-700">
                      Once you receive your order, confirm delivery to release payment to the supplier.
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm('Confirm that you have received this order? This will release the payment to the supplier.')) return;
                      try {
                        setConfirmingDelivery(true);
                        const orderId = selectedOrder.id;
                        await marketAPI.confirmDelivery(orderId);
                        toast.success('Delivery confirmed! Payment released to supplier.');
                        // Optimistic update for instant UI feedback
                        setMyOrders(prevOrders => 
                          prevOrders.map(o => o.id === orderId ? { ...o, status: 'delivered' } : o)
                        );
                        setSelectedOrder(null);
                        loadMyOrders();
                      } catch (error) {
                        toast.error(error.response?.data?.message || 'Failed to confirm delivery');
                      } finally {
                        setConfirmingDelivery(false);
                      }
                    }}
                    disabled={confirmingDelivery}
                    className="w-full px-4 py-2.5 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
                  >
                    {confirmingDelivery ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        Confirm Delivery Received
                      </>
                    )}
                  </button>
                  <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-2">
                    Payment will auto-release in 7 days if not confirmed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && payingOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Pay for Order #{payingOrder.orderNumber}</h3>
              <p className="text-xs sm:text-sm text-gray-500">Amount: KES {Number(payingOrder.totalAmount).toLocaleString()}</p>
            </div>
            
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Escrow info */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3">
                <p className="text-xs sm:text-sm text-amber-800 flex items-start gap-2">
                  <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  Payment is protected by escrow. Funds released only after you confirm delivery.
                </p>
              </div>
              
              {!mpesaSent ? (
                <>
                  {/* Payment method selection */}
                  <div className="space-y-2 sm:space-y-3">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700">Payment Method</label>
                    
                    {/* M-Pesa option */}
                    <label className={`flex items-center p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${
                      paymentMethod === 'mpesa' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="mpesa"
                        checked={paymentMethod === 'mpesa'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="h-4 w-4 text-green-600"
                      />
                      <div className="ml-3 flex-1">
                        <span className="font-medium text-gray-900 text-sm">📱 M-Pesa</span>
                        <p className="text-xs text-gray-500 mt-0.5">Pay instantly via STK push</p>
                      </div>
                    </label>
                    
                    {/* Wallet option */}
                    <label className={`flex items-center p-3 sm:p-4 border rounded-lg cursor-pointer transition-colors ${
                      paymentMethod === 'wallet' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                    } ${walletBalance < Number(payingOrder.totalAmount) ? 'opacity-50' : ''}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="wallet"
                        checked={paymentMethod === 'wallet'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        disabled={walletBalance < Number(payingOrder.totalAmount)}
                        className="h-4 w-4 text-primary-600"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-gray-900 flex items-center text-sm">
                            <WalletIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-primary-600" />
                            Wallet
                          </span>
                          <span className={`text-xs sm:text-sm ${walletBalance >= Number(payingOrder.totalAmount) ? 'text-green-600' : 'text-red-600'}`}>
                            KES {walletBalance.toLocaleString()}
                          </span>
                        </div>
                        {walletBalance < Number(payingOrder.totalAmount) && (
                          <p className="text-xs text-red-500 mt-0.5">Insufficient balance</p>
                        )}
                      </div>
                    </label>
                  </div>
                  
                  {/* M-Pesa phone input */}
                  {paymentMethod === 'mpesa' && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">M-Pesa Phone Number</label>
                      <input
                        type="tel"
                        value={mpesaPhone}
                        onChange={(e) => setMpesaPhone(e.target.value)}
                        placeholder="0712345678"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  )}
                </>
              ) : (
                /* M-Pesa sent - waiting for confirmation */
                <div className="text-center py-4">
                  <div className="animate-pulse mb-3 sm:mb-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                      <PhoneIcon className="h-7 w-7 sm:h-8 sm:w-8 text-green-600" />
                    </div>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Check your phone</h4>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                    An M-Pesa prompt has been sent to {mpesaPhone}.<br/>
                    Enter your PIN to complete payment.
                  </p>
                  {checkingPayment && (
                    <p className="text-xs sm:text-sm text-blue-600 flex items-center justify-center gap-2">
                      <LoadingSpinner className="w-4 h-4" />
                      Checking payment status...
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-4 sm:p-6 border-t border-gray-200 flex gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPayingOrder(null);
                  setMpesaSent(false);
                }}
                className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                disabled={paymentProcessing}
              >
                Cancel
              </button>
              
              {!mpesaSent ? (
                <button
                  onClick={async () => {
                    setPaymentProcessing(true);
                    try {
                      if (paymentMethod === 'wallet') {
                        await marketAPI.payWithWallet(payingOrder.id);
                        toast.success('Payment successful! Funds held in escrow.');
                        setShowPaymentModal(false);
                        setPayingOrder(null);
                        setSelectedOrder(null);
                        loadMyOrders();
                      } else {
                        // M-Pesa
                        if (!mpesaPhone) {
                          toast.error('Please enter your M-Pesa phone number');
                          setPaymentProcessing(false);
                          return;
                        }
                        await marketAPI.payWithMpesa(payingOrder.id, mpesaPhone);
                        setMpesaSent(true);
                        toast.success('M-Pesa prompt sent! Check your phone.');
                        
                        // Start polling for payment status
                        let attempts = 0;
                        const maxAttempts = 30; // 60 seconds
                        const pollInterval = setInterval(async () => {
                          attempts++;
                          setCheckingPayment(true);
                          try {
                            const statusRes = await marketAPI.checkPaymentStatus(payingOrder.id);
                            if (statusRes.data.paid) {
                              clearInterval(pollInterval);
                              setCheckingPayment(false);
                              toast.success('Payment confirmed! Funds held in escrow.');
                              setShowPaymentModal(false);
                              setPayingOrder(null);
                              setSelectedOrder(null);
                              loadMyOrders();
                            }
                          } catch (e) {
                            console.error('Payment status check error:', e);
                          }
                          if (attempts >= maxAttempts) {
                            clearInterval(pollInterval);
                            setCheckingPayment(false);
                          }
                        }, 2000);
                      }
                    } catch (error) {
                      toast.error(error.response?.data?.message || 'Payment failed');
                    } finally {
                      setPaymentProcessing(false);
                    }
                  }}
                  disabled={paymentProcessing || (paymentMethod === 'wallet' && walletBalance < Number(payingOrder.totalAmount))}
                  className="flex-1 px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                >
                  {paymentProcessing ? (
                    <>
                      <LoadingSpinner className="w-4 h-4" />
                      Processing...
                    </>
                  ) : (
                    `Pay KES ${Number(payingOrder.totalAmount).toLocaleString()}`
                  )}
                </button>
              ) : (
                <button
                  onClick={() => {
                    // Manually check payment status
                    setCheckingPayment(true);
                    marketAPI.checkPaymentStatus(payingOrder.id).then(res => {
                      setCheckingPayment(false);
                      if (res.data.paid) {
                        toast.success('Payment confirmed!');
                        setShowPaymentModal(false);
                        setPayingOrder(null);
                        setSelectedOrder(null);
                        loadMyOrders();
                      } else {
                        toast.error('Payment not yet received. Please complete the M-Pesa prompt.');
                      }
                    }).catch(() => {
                      setCheckingPayment(false);
                      toast.error('Failed to check payment status');
                    });
                  }}
                  disabled={checkingPayment}
                  className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                >
                  {checkingPayment ? (
                    <>
                      <LoadingSpinner className="w-4 h-4" />
                      Checking...
                    </>
                  ) : (
                    "I've Paid"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && selectedSupplier && (
        <CheckoutModal
          supplier={selectedSupplier}
          cart={cart}
          cartTotal={cartTotal}
          onClose={() => setShowCheckout(false)}
          onSubmit={submitOrder}
          submitting={submitting}
          user={user}
        />
      )}
    </div>
  );
};

// Checkout Modal Component - Places order without immediate payment
const CheckoutModal = ({ supplier, cart, cartTotal, onClose, onSubmit, submitting, user }) => {
  const [formData, setFormData] = useState({
    fulfillmentType: 'delivery',
    paymentMethod: 'later', // Always 'later' - payment happens from orders list
    contactName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '',
    contactPhone: user?.phone || '',
    contactEmail: user?.email || '',
    deliveryAddress: user?.address || '',
    deliveryCounty: user?.county || '',
    deliveryTown: user?.town || '',
    deliveryNotes: '',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const deliveryFee = supplier.deliveryAvailable && formData.fulfillmentType === 'delivery' 
    ? Number(supplier.deliveryFee || 0) 
    : 0;
  const total = cartTotal + deliveryFee;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Complete Your Order</h3>
          <p className="text-xs sm:text-sm text-gray-500">Ordering from {supplier.businessName}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Order Summary */}
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
            <h4 className="font-medium text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Order Summary</h4>
            <div className="space-y-1.5 sm:space-y-2">
              {cart.map(c => (
                <div key={c.product.id} className="flex justify-between text-xs sm:text-sm">
                  <span className="flex-1 min-w-0 truncate mr-2">{c.quantity}x {c.product.name}</span>
                  <span className="flex-shrink-0">KES {(Number(c.product.price) * c.quantity).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200 space-y-1">
              <div className="flex justify-between text-xs sm:text-sm">
                <span>Subtotal</span>
                <span>KES {cartTotal.toLocaleString()}</span>
              </div>
              {deliveryFee > 0 && (
                <div className="flex justify-between text-xs sm:text-sm">
                  <span>Delivery Fee</span>
                  <span>KES {deliveryFee.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-sm">
                <span>Total</span>
                <span>KES {total.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Flow Info */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <ShieldCheckIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">How Payment Works</p>
                <ol className="text-xs text-blue-600 mt-1.5 space-y-1 list-decimal list-inside">
                  <li>Place your order (no payment now)</li>
                  <li>Supplier confirms your order</li>
                  <li>Pay via M-Pesa or Wallet from the Orders tab</li>
                  <li>Funds held in escrow until you confirm delivery</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Fulfillment Type */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">Fulfillment Method</label>
            <div className="flex gap-4">
              {supplier.deliveryAvailable && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="fulfillmentType"
                    value="delivery"
                    checked={formData.fulfillmentType === 'delivery'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-xs sm:text-sm text-gray-700">Delivery</span>
                </label>
              )}
              {supplier.pickupAvailable && (
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="fulfillmentType"
                    value="pickup"
                    checked={formData.fulfillmentType === 'pickup'}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-xs sm:text-sm text-gray-700">Pickup</span>
                </label>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Your Name *</label>
              <input
                type="text"
                name="contactName"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="contactPhone"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactPhone}
                onChange={handleChange}
                placeholder="+254..."
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="contactEmail"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.contactEmail}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Delivery Address */}
          {formData.fulfillmentType === 'delivery' && (
            <div className="space-y-3 sm:space-y-4">
              <h4 className="font-medium text-gray-900 text-sm sm:text-base">Delivery Address</h4>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">County</label>
                  <select
                    name="deliveryCounty"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.deliveryCounty}
                    onChange={handleChange}
                  >
                    <option value="">Select county</option>
                    {KENYA_COUNTIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Town</label>
                  <input
                    type="text"
                    name="deliveryTown"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.deliveryTown}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Full Address *</label>
                <textarea
                  name="deliveryAddress"
                  rows={2}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.deliveryAddress}
                  onChange={handleChange}
                  placeholder="Building, street, landmarks..."
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Delivery Notes</label>
                <textarea
                  name="deliveryNotes"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.deliveryNotes}
                  onChange={handleChange}
                  placeholder="Any special instructions..."
                />
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
            <textarea
              name="notes"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.notes}
              onChange={handleChange}
            />
          </div>

          <div className="flex gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Placing Order...' : `Place Order - KES ${total.toLocaleString()}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Market;
